import { useState, useEffect } from 'react';
import { Cookie, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CONSENT_KEY = 'ziko_cookie_consent';

export default function CookieBanner() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // All localStorage access INSIDE useEffect — never at module level
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) {
        const t = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  const saveConsent = (anal: boolean, mkt: boolean) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({
        necessary: true, analytics: anal, marketing: mkt, timestamp: Date.now()
      }));
    } catch {}
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('consent', 'update', {
        analytics_storage: anal ? 'granted' : 'denied',
        ad_storage: mkt ? 'granted' : 'denied',
        ad_user_data: mkt ? 'granted' : 'denied',
        ad_personalization: mkt ? 'granted' : 'denied',
      });
    }
    setVisible(false);
  };

  if (!visible) return null;

  const Toggle = ({ checked, onChange, disabled = false }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-primary' : 'bg-white/10'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[200] p-4 md:p-0 md:bottom-6 md:left-6 md:right-auto md:max-w-sm"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cookie className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">
              {isAr ? 'نحن نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
            </p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {isAr ? 'نستخدمها لتحسين تجربتك وتحليل حركة الزيارات.' : 'We use cookies to enhance your experience and analyze traffic.'}
            </p>
          </div>
        </div>

        {expanded && (
          <div className="px-5 pb-4 space-y-3 border-t border-border/40 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Shield className="h-3 w-3 text-emerald-400" />
                  {isAr ? 'ضروري دائماً' : 'Necessary (always on)'}
                </p>
              </div>
              <Toggle checked={true} onChange={() => {}} disabled={true} />
            </div>
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-semibold text-foreground">{isAr ? 'التحليلات' : 'Analytics'}</p>
              <Toggle checked={analytics} onChange={setAnalytics} />
            </div>
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-semibold text-foreground">{isAr ? 'التسويق' : 'Marketing'}</p>
              <Toggle checked={marketing} onChange={setMarketing} />
            </div>
          </div>
        )}

        <div className="px-5 pb-5 flex flex-col gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 self-start transition-colors mb-1"
          >
            {expanded ? (isAr ? 'إخفاء' : 'Hide options') : (isAr ? 'إدارة التفضيلات' : 'Manage preferences')}
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
          <div className="flex gap-2">
            {expanded ? (
              <>
                <button onClick={() => saveConsent(false, false)} className="flex-1 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-secondary/50 transition-colors">
                  {isAr ? 'الضروري فقط' : 'Necessary only'}
                </button>
                <button onClick={() => saveConsent(analytics, marketing)} className="flex-1 px-3 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium hover:bg-primary/20 transition-colors">
                  {isAr ? 'حفظ' : 'Save'}
                </button>
                <button onClick={() => saveConsent(true, true)} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:brightness-110 transition-all">
                  {isAr ? 'قبول الكل' : 'Accept all'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => saveConsent(false, false)} className="flex-1 px-3 py-2 rounded-lg border border-border text-xs font-medium hover:bg-secondary/50 transition-colors">
                  {isAr ? 'رفض' : 'Decline'}
                </button>
                <button onClick={() => saveConsent(true, true)} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:brightness-110 transition-all">
                  {isAr ? 'قبول الكل' : 'Accept all'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
